import {
  Group,
  Button,
  Text,
  Stack,
  Alert,
  ThemeIcon,
} from '@mantine/core';
import { Dropzone } from '@mantine/dropzone';
import {
  Upload,
  FileText,
  Download,
  Info,
} from 'react-feather';
import classes from '../ImportParticipantsModal.module.css';

interface UploadStepProps {
  onFileDrop: (files: File[]) => Promise<void>;
  file: File | null;
  csvDataRowCount: number;
  existingCount: number;
  isReupload: boolean;
}

export function UploadStep({
  onFileDrop,
  file,
  csvDataRowCount,
  existingCount,
  isReupload,
}: UploadStepProps) {
  return (
    <Stack gap="md">
      {isReupload && (
        <Alert
          icon={<Info size={16} />}
          color="blue"
          variant="light"
          title="Updating Existing Participants"
        >
          This cohort already has {existingCount} participants. Uploading a CSV will:
          <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
            <li>Update existing participants matched by email</li>
            <li>Add any new participants not already in the cohort</li>
            <li>Leave existing participants not in the CSV unchanged</li>
          </ul>
          Only non-empty CSV values will overwrite existing data.
        </Alert>
      )}
      <Dropzone
        onDrop={onFileDrop}
        accept={{ 'text/csv': ['.csv'] }}
        maxFiles={1}
        className={file ? classes.dropzoneWithFile : classes.dropzone}
      >
        {file ? (
          <Stack align="center" gap="xs" py="xl">
            <ThemeIcon size={60} radius={60} variant="light" color="medleyGreen">
              <FileText size={30} />
            </ThemeIcon>
            <Text fw={500}>{file.name}</Text>
            <Text size="sm" c="dimmed">
              {csvDataRowCount} rows detected
            </Text>
            <Text size="sm" c="medleyGreen" style={{ cursor: 'pointer' }}>
              Choose different file
            </Text>
          </Stack>
        ) : (
          <Stack align="center" gap="xs" py="xl">
            <Upload size={32} color="gray" />
            <Text fw={500}>Drag and drop your CSV file here</Text>
            <Text size="sm" c="dimmed">
              or click to browse
            </Text>
          </Stack>
        )}
      </Dropzone>

      <Group justify="center">
        <Button
          variant="subtle"
          leftSection={<Download size={16} />}
          onClick={() => {
            // Download template CSV
            const headers = ['Email', 'First Name', 'Last Name', 'Preferred Name', 'i v e', 'opco', 'role', 'location', 'manager', 'level', 'DNM', 'DNM groupings'];
            const csv = headers.join(',');
            const blob = new Blob([csv], { type: 'text/csv' });
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = 'participant-template.csv';
            a.click();
          }}
        >
          Download template CSV
        </Button>
      </Group>
    </Stack>
  );
}
